/**
 * Predictive Models → detail → "Training data preview" section.
 *
 * Surfaces the per-tenant Redshift training mart for one use case so the
 * customer admin can see — at a glance — whether they've actually loaded
 * data into the mart, which columns are populated and how thoroughly,
 * and what the rows actually look like.
 *
 * Three render states (driven by the backend `health` block):
 *   1. unreachable     — `health.exists=false && health.error`. Render
 *                        the warehouse error verbatim so admins can pass
 *                        it to us when filing tickets.
 *   2. unprovisioned   — `health.exists=false && !health.error`. The
 *                        feature-flag hook should provision on enable;
 *                        if we land here it means provisioning failed.
 *                        Show a "re-toggle the feature" hint.
 *   3. healthy         — `health.exists=true`. Show the column list
 *                        with fill-rate badges + a sample-rows table.
 *
 * UX choices
 * ----------
 * - Columns are grouped by role (identifier / target / feature /
 *   metadata) because that's how a customer reasons about the mart
 *   ("which columns are inputs vs outputs?"). Within a role group we
 *   sort by declaration order from the YAML (preserves the curated
 *   layout — alphabetisation here would scatter related columns).
 * - Fill-rate is rendered as `<n>/<total> (<pct>%)` plus a coloured
 *   chip. Threshold colours track the validation gates downstream:
 *   ≥80% green, 40-80% amber, <40% red. These thresholds are
 *   informational; the engine drops a feature only at 0% (entirely
 *   absent), not at low fill rates.
 * - Sample rows render in a virtualised-friendly way (no fixed-height
 *   container; the parent Accordion can scroll). For the demo we cap
 *   at 25 rows; production users iterate via the row-count + fill-rate
 *   summary, not by paginating raw rows.
 */
import React from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Divider,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from '@mui/material';
import RefreshRounded from '@mui/icons-material/RefreshRounded';
import StorageRounded from '@mui/icons-material/StorageRounded';
import {
  getMartPreview,
  MartColumnPreview,
  MartColumnRole,
  MartPreview,
} from '../../../services/propensityService';

interface Props {
  tenantId: string;
  configId: string;
}

const ROLE_ORDER: MartColumnRole[] = ['identifier', 'target', 'feature', 'metadata'];

const ROLE_LABEL: Record<MartColumnRole, string> = {
  identifier: 'Identifiers',
  target: 'Target',
  feature: 'Features',
  metadata: 'Metadata',
};

const ROLE_DESCRIPTION: Record<MartColumnRole, string> = {
  identifier: 'Primary keys + snapshot timestamps. Excluded from the model; surfaced in chat results.',
  target: 'Binary outcome the model is trained to predict.',
  feature: 'Eligible model inputs. Customers fill in what they have; missing columns stay NULL.',
  metadata: 'Audit / provenance columns. The engine ignores these.',
};

const ROLE_CHIP_COLOR: Record<MartColumnRole, 'default' | 'primary' | 'success' | 'warning'> = {
  identifier: 'default',
  target: 'primary',
  feature: 'success',
  metadata: 'warning',
};

function fillBucket(pct: number): { color: 'success' | 'warning' | 'error' | 'default'; label: string } {
  if (Number.isNaN(pct)) return { color: 'default', label: '—' };
  if (pct >= 80) return { color: 'success', label: `${pct.toFixed(0)}%` };
  if (pct >= 40) return { color: 'warning', label: `${pct.toFixed(0)}%` };
  return { color: 'error', label: `${pct.toFixed(0)}%` };
}

function formatCellValue(v: unknown): string {
  if (v === null || v === undefined) return '—';
  if (typeof v === 'number') return v.toLocaleString();
  if (typeof v === 'boolean') return v ? 'true' : 'false';
  return String(v);
}

export default function MartPreviewPanel({ tenantId, configId }: Props) {
  const [preview, setPreview] = React.useState<MartPreview | null>(null);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState<string | null>(null);

  const refresh = React.useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const got = await getMartPreview(tenantId, configId, { sampleLimit: 25 });
      setPreview(got);
    } catch (e: any) {
      setError(e?.message || 'Failed to load training data preview');
    } finally {
      setLoading(false);
    }
  }, [tenantId, configId]);

  React.useEffect(() => { void refresh(); }, [refresh]);

  if (loading && !preview) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
        <CircularProgress size={20} />
      </Box>
    );
  }

  if (error) {
    return (
      <Alert
        severity="error"
        action={
          <Button size="small" startIcon={<RefreshRounded />} onClick={refresh}>
            Retry
          </Button>
        }
      >
        {error}
      </Alert>
    );
  }

  if (!preview) return null;

  const { schema, health, fill_rates, sample_rows } = preview;

  // Header — always visible, regardless of mart state.
  const header = (
    <Stack direction="row" alignItems="center" spacing={1.5} sx={{ mb: 2 }}>
      <Box
        sx={(t) => ({
          width: 32,
          height: 32,
          borderRadius: `${t.ds.radius.sm}px`,
          bgcolor: t.tokens.brand.primarySoft,
          color: t.tokens.brand.primary,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          flexShrink: 0,
        })}
      >
        <StorageRounded sx={{ fontSize: 18 }} />
      </Box>
      <Box sx={{ minWidth: 0, flex: 1 }}>
        <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
          {schema.schema_name}.{schema.table_name}
        </Typography>
        <Typography variant="caption" color="text.secondary">
          {schema.description}
        </Typography>
      </Box>
      <Tooltip title="Refresh">
        <Button size="small" startIcon={<RefreshRounded />} onClick={refresh} disabled={loading}>
          Refresh
        </Button>
      </Tooltip>
    </Stack>
  );

  // State 1: unreachable warehouse.
  if (health.error) {
    return (
      <Box>
        {header}
        <Alert severity="error" sx={{ mb: 2 }}>
          <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5 }}>
            Could not connect to the propensity warehouse.
          </Typography>
          <Typography variant="caption" component="div" sx={{ wordBreak: 'break-word' }}>
            {health.error}
          </Typography>
        </Alert>
        <ColumnList columns={schema.columns} fill_rates={fill_rates} hideFillRates />
      </Box>
    );
  }

  // State 2: warehouse reachable, table not provisioned.
  if (!health.exists) {
    return (
      <Box>
        {header}
        <Alert severity="warning" sx={{ mb: 2 }}>
          <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5 }}>
            Mart table not yet provisioned.
          </Typography>
          <Typography variant="caption">
            The {schema.table_name} table doesn't exist in {schema.schema_name}.
            Provisioning runs automatically when the Propensity Scoring feature
            is enabled — disable and re-enable the feature in
            <strong> Settings → Features</strong> to retry.
          </Typography>
        </Alert>
        <ColumnList columns={schema.columns} fill_rates={fill_rates} hideFillRates />
      </Box>
    );
  }

  // State 3: healthy.
  return (
    <Box>
      {header}
      <Stack direction="row" spacing={3} sx={{ mb: 2 }}>
        <Stat label="Total rows" value={(health.row_count ?? 0).toLocaleString()} />
        <Stat
          label="Columns"
          value={`${schema.columns.length} (${schema.columns.filter(c => c.role === 'feature').length} features)`}
        />
        <Stat
          label="Status"
          value={health.has_data ? 'Loaded' : 'Empty'}
          color={health.has_data ? 'success' : 'warning'}
        />
      </Stack>

      {!health.has_data && (
        <Alert severity="info" sx={{ mb: 2 }}>
          The mart table exists but is empty. Load training rows into{' '}
          <code>{schema.schema_name}.{schema.table_name}</code> before running validation.
        </Alert>
      )}

      <ColumnList columns={schema.columns} fill_rates={fill_rates} />

      {sample_rows.length > 0 && (
        <Box sx={{ mt: 3 }}>
          <Typography
            variant="overline"
            color="text.secondary"
            sx={{ display: 'block', mb: 1 }}
          >
            Sample rows (first {sample_rows.length})
          </Typography>
          <SampleRowsTable rows={sample_rows} columns={schema.columns} />
        </Box>
      )}
    </Box>
  );
}

// ---------------------------------------------------------------------------
// Column list grouped by role
// ---------------------------------------------------------------------------

function ColumnList({
  columns,
  fill_rates,
  hideFillRates = false,
}: {
  columns: MartColumnPreview[];
  fill_rates: Record<string, { non_null: number; total: number }>;
  hideFillRates?: boolean;
}) {
  const grouped = React.useMemo(() => {
    const m: Record<MartColumnRole, MartColumnPreview[]> = {
      identifier: [],
      target: [],
      feature: [],
      metadata: [],
    };
    for (const c of columns) m[c.role].push(c);
    return m;
  }, [columns]);

  return (
    <Box>
      {ROLE_ORDER.map((role) => {
        const cols = grouped[role];
        if (cols.length === 0) return null;
        return (
          <Box key={role} sx={{ mb: 2.5 }}>
            <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 0.75 }}>
              <Typography
                variant="overline"
                sx={(t) => ({ fontWeight: 700, color: t.tokens.text.primary })}
              >
                {ROLE_LABEL[role]}
              </Typography>
              <Chip
                size="small"
                label={cols.length}
                color={ROLE_CHIP_COLOR[role]}
                sx={{ height: 18, fontSize: '0.6875rem' }}
              />
            </Stack>
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ display: 'block', mb: 1 }}
            >
              {ROLE_DESCRIPTION[role]}
            </Typography>
            <TableContainer
              component={Box}
              sx={(t) => ({
                border: `1px solid ${t.tokens.border.subtle}`,
                borderRadius: `${t.ds.radius.sm}px`,
                overflow: 'hidden',
              })}
            >
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell width="22%">Column</TableCell>
                    <TableCell width="18%">Type</TableCell>
                    <TableCell width="14%">Semantic</TableCell>
                    {!hideFillRates && <TableCell width="14%">Fill rate</TableCell>}
                    <TableCell>Description</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {cols.map((c) => {
                    const fr = fill_rates[c.name];
                    const pct = fr && fr.total > 0
                      ? (fr.non_null / fr.total) * 100
                      : NaN;
                    const bucket = fillBucket(pct);
                    return (
                      <TableRow key={c.name}>
                        <TableCell>
                          <Typography variant="body2" sx={{ fontWeight: 500, fontFamily: 'monospace', fontSize: '0.8125rem' }}>
                            {c.name}
                          </Typography>
                          {!c.nullable && (
                            <Chip
                              size="small"
                              label="NOT NULL"
                              variant="outlined"
                              sx={{ height: 16, fontSize: '0.625rem', ml: 0.5 }}
                            />
                          )}
                        </TableCell>
                        <TableCell>
                          <Typography variant="caption" sx={{ fontFamily: 'monospace' }}>
                            {c.sql_type}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="caption" color="text.secondary">
                            {c.semantic_type || '—'}
                          </Typography>
                        </TableCell>
                        {!hideFillRates && (
                          <TableCell>
                            {fr ? (
                              <Tooltip title={`${fr.non_null.toLocaleString()} non-null of ${fr.total.toLocaleString()}`}>
                                <Chip
                                  size="small"
                                  label={bucket.label}
                                  color={bucket.color}
                                  variant={bucket.color === 'default' ? 'outlined' : 'filled'}
                                  sx={{ height: 20, fontSize: '0.6875rem', fontWeight: 600 }}
                                />
                              </Tooltip>
                            ) : (
                              <Typography variant="caption" color="text.secondary">—</Typography>
                            )}
                          </TableCell>
                        )}
                        <TableCell>
                          <Typography variant="caption" color="text.secondary">
                            {c.description || '—'}
                          </Typography>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          </Box>
        );
      })}
    </Box>
  );
}

function Stat({
  label,
  value,
  color,
}: {
  label: string;
  value: React.ReactNode;
  color?: 'success' | 'warning' | 'error';
}) {
  return (
    <Box>
      <Typography variant="overline" color="text.secondary" sx={{ display: 'block' }}>
        {label}
      </Typography>
      <Typography
        variant="body1"
        sx={(t) => ({
          fontWeight: 600,
          color: color === 'success'
            ? t.tokens.semantic.success
            : color === 'warning'
              ? t.tokens.semantic.warning
              : color === 'error'
                ? t.tokens.semantic.danger
                : 'inherit',
        })}
      >
        {value}
      </Typography>
    </Box>
  );
}

function SampleRowsTable({
  rows,
  columns,
}: {
  rows: Array<Record<string, unknown>>;
  columns: MartColumnPreview[];
}) {
  // Use the YAML's column order (preserves curated layout) but only
  // include columns the backend returned in `sample_rows` — older
  // configs may have YAMLs that drift from what Redshift was
  // provisioned with.
  const orderedKeys = React.useMemo(() => {
    if (rows.length === 0) return [] as string[];
    const presentKeys = new Set(Object.keys(rows[0]));
    return columns.map((c) => c.name).filter((n) => presentKeys.has(n));
  }, [rows, columns]);

  if (rows.length === 0) return null;
  return (
    <TableContainer
      component={Box}
      sx={(t) => ({
        border: `1px solid ${t.tokens.border.subtle}`,
        borderRadius: `${t.ds.radius.sm}px`,
        overflowX: 'auto',
        maxWidth: '100%',
      })}
    >
      <Table size="small" sx={{ minWidth: 800 }}>
        <TableHead>
          <TableRow>
            {orderedKeys.map((k) => (
              <TableCell key={k} sx={{ whiteSpace: 'nowrap' }}>
                <Typography variant="caption" sx={{ fontFamily: 'monospace', fontWeight: 600 }}>
                  {k}
                </Typography>
              </TableCell>
            ))}
          </TableRow>
        </TableHead>
        <TableBody>
          {rows.map((row, idx) => (
            <TableRow key={idx} hover>
              {orderedKeys.map((k) => (
                <TableCell key={k} sx={{ whiteSpace: 'nowrap', maxWidth: 240, overflow: 'hidden', textOverflow: 'ellipsis' }}>
                  <Typography variant="caption" sx={{ fontFamily: 'monospace' }}>
                    {formatCellValue(row[k])}
                  </Typography>
                </TableCell>
              ))}
            </TableRow>
          ))}
        </TableBody>
      </Table>
      <Divider />
      <Box sx={{ p: 1, textAlign: 'center' }}>
        <Typography variant="caption" color="text.secondary">
          Showing {rows.length} sample rows
        </Typography>
      </Box>
    </TableContainer>
  );
}
